/**
 * Flatten complex / guided product configuration into SKU / product IDs for shipping APIs.
 *
 * The result is **one group** of IDs for a single PDP configuration (variant + recommendation /
 * component SKUs such as shelves, cushions, legs). Send it as one `cartItems` row with
 * `productIds: extractDecomposedProductIds(...)`; checkout uses one row per basket line, each with
 * its own `productIds` when decomposed.
 *
 * @param {Object} product - Master SCAPI product
 * @param {import('../context/customize-journey-context').SelectionsMap} selections - Committed selections (cover + option slots)
 * @returns {string[]} Deduplicated IDs (master first, then cover variant, then option products)
 */
export function extractDecomposedProductIds(product, selections) {
  const ids = [];
  const masterId = product?.id || product?.productId;

  if (masterId) {
    ids.push(String(masterId));
  }

  if (selections?.cover) {
    const coverId = selections.cover.variantId || selections.cover.productId;

    if (coverId) {
      ids.push(String(coverId));
    }
  }

  Object.entries(selections || {}).forEach(([stepId, slotSelections]) => {
    if (stepId === 'cover' || !Array.isArray(slotSelections)) return;

    slotSelections.forEach((slot) => {
      if (slot?.productId) {
        ids.push(String(slot.productId));
      }
    });
  });

  const extra = product?.c_pdpShippingExtraProductIds;

  if (Array.isArray(extra)) {
    extra.forEach((id) => {
      if (id) ids.push(String(id));
    });
  } else if (typeof extra === 'string' && extra.trim()) {
    try {
      const parsed = JSON.parse(extra);

      if (Array.isArray(parsed)) {
        parsed.forEach((id) => {
          if (id) ids.push(String(id));
        });
      }
    } catch {
      // ignore invalid JSON
    }
  }

  return [...new Set(ids)];
}
